// Buyer documents of a sourcing event (Lane 1331, Phase 2 slice 2E-2): PDF,
// Excel, Word, PNG or JPEG files, up to 25 MB and 50 per event. Each is
// marked for suppliers or for the team only, and "for information only" (a
// file that does not change what suppliers quote). On a draft they change
// directly; after publish only on a change in progress, and suppliers see
// them when the change is published.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDueAt } from '../sourcing-portal/portalView';
import { STALE_MESSAGE, formatDate } from './sourcingApi';

const ACCEPT = '.pdf,.xlsx,.docx,.png,.jpg,.jpeg';

export function sizeText(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// The file type in words, from the type the server stored.
const TYPE_TEXT = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'image/png': 'PNG image',
  'image/jpeg': 'JPEG image',
};
function typeText(d) {
  return TYPE_TEXT[d.content_type] || 'File';
}

// Lane 1387 (Ryan 2026-10-05): the event's own "Documents" tab (it was the lower half
// of "Terms and documents"). A table: name (click to download), type, size, the date it
// was added, whether suppliers see it and whether it is for information only. The buyer
// uploads, sets both flags with the row's checkboxes, replaces and removes here, with the
// same actions and the same rules as before.
export default function DocumentsTab({ api, eventId, summary, onChanged }) {
  const live = ['open', 'closed'].includes(summary.status);
  const amending = summary.status !== 'draft' && Boolean(summary.amendment);
  const editable = summary.status === 'draft' || amending;
  const version = amending ? 'amendment' : 'published';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(true);
  const [informational, setInformational] = useState(false);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const [replacing, setReplacing] = useState(null);

  const load = useCallback(() => api.documents(eventId, version)
    .then((r) => { setData(r); })
    .catch((e) => setError(e.message)), [api, eventId, version]);
  useEffect(() => { load(); }, [load, summary.revision]);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await onChanged();
      await load();
    } catch (e) {
      if (e.code === 'stale_revision') await onChanged();
      setError(e.code === 'stale_revision' ? STALE_MESSAGE : e.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
      if (replaceRef.current) replaceRef.current.value = '';
    }
  };

  const items = (data && data.items) || [];
  const full = data && items.length >= data.max_documents;
  // Each flag change keeps the rules it always had: hiding a document also clears "for
  // information only"; only a document suppliers see can be for information only.
  const toggleVisible = (d) => run(() => api.documentAction(eventId, d.document_id, 'flags',
    { supplier_visible: !d.supplier_visible, informational: false }, summary.revision));
  const toggleInfo = (d) => run(() => api.documentAction(eventId, d.document_id, 'flags',
    { supplier_visible: true, informational: !d.informational }, summary.revision));
  const yesNo = (on) => (on ? 'Yes' : 'No');
  const grid = { gridTemplateColumns: `minmax(0, 2.4fr) minmax(0, 0.8fr) minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.1fr)${editable ? ' 170px' : ''}` };
  return (
    <section className="srce-card2" data-testid="srce-documents">
      <div className="srce-card2-h srce-docs-h">
        <span className="srce-mut srce-docs-note">
          {summary.status === 'draft' && 'Suppliers see only the documents you mark for them, when the event is published.'}
          {amending && 'You are editing the change in progress. Suppliers see only the documents marked for them, after you publish the change.'}
          {!editable && summary.status !== 'draft' && (live
            ? 'To add or change a document after publish, click Change the event at the top.'
            : 'This event is finished; its documents are kept here to read.')}
        </span>
        {editable && (
          <span className="srce-push srce-row">
            <label className="srce-inline">
              <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} /> Suppliers can see it
            </label>
            <label className="srce-inline">
              <input type="checkbox" checked={visible && informational} disabled={!visible}
                onChange={(e) => setInformational(e.target.checked)} /> For information only (in a change, suppliers' offers still count by default)
            </label>
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy || full}
              onClick={() => fileRef.current && fileRef.current.click()}>Upload a document</button>
          </span>
        )}
        <input ref={fileRef} type="file" accept={ACCEPT} hidden
          onChange={(e) => e.target.files[0] && run(() => api.uploadDocument(eventId, e.target.files[0],
            summary.revision, { supplier_visible: visible, informational: visible && informational }))} />
        <input ref={replaceRef} type="file" accept={ACCEPT} hidden
          onChange={(e) => e.target.files[0] && replacing && run(() => api.replaceDocument(eventId, replacing,
            e.target.files[0], summary.revision))} />
      </div>
      {error && <div className="srce-error" role="alert">{error}</div>}
      {full && <div className="srce-mut srce-card2-f">This event has the most documents it can hold.</div>}
      <div className="srce-wt" role="table" aria-label="Documents">
        <div className="srce-wt-row srce-wt-head" style={grid} role="row">
          <div className="srce-wt-cell" role="columnheader">Document</div>
          <div className="srce-wt-cell" role="columnheader">Type</div>
          <div className="srce-wt-cell srce-wt-num" role="columnheader">Size</div>
          <div className="srce-wt-cell" role="columnheader">Added</div>
          <div className="srce-wt-cell" role="columnheader" title="Suppliers see it">Suppliers see it</div>
          <div className="srce-wt-cell" role="columnheader" title="For information only: it does not change what suppliers quote">For information only</div>
          {editable && <div className="srce-wt-cell" role="columnheader"><span className="srce-sr">Actions</span></div>}
        </div>
        {!data && !error && <div className="ll-loading srce-wt-empty">Loading documents…</div>}
        {data && items.length === 0 && <div className="srce-mut srce-wt-empty">No documents yet.</div>}
        {items.map((d) => (
          <div key={d.document_id} className="srce-wt-row" style={grid} role="row">
            <div className="srce-wt-cell" role="cell">
              <button type="button" className="ll-clr srce-docs-name" title={`Download ${d.file_name}`}
                onClick={() => api.downloadDocument(eventId, d.document_id, version)
                  .then((b) => saveBlob(b, d.file_name)).catch((e) => setError(e.message))}>{d.file_name}</button>
            </div>
            <div className="srce-wt-cell" role="cell">{typeText(d)}</div>
            <div className="srce-wt-cell srce-wt-num" role="cell">{sizeText(d.size_bytes)}</div>
            <div className="srce-wt-cell" role="cell" title={formatDueAt(d.added_at)}>{formatDate(d.added_at)}</div>
            <div className="srce-wt-cell" role="cell">
              {editable ? (
                <label className="srce-inline">
                  <input type="checkbox" checked={d.supplier_visible} disabled={busy}
                    aria-label={`Suppliers see ${d.file_name}`} onChange={() => toggleVisible(d)} />
                  {yesNo(d.supplier_visible)}
                </label>
              ) : yesNo(d.supplier_visible)}
            </div>
            <div className="srce-wt-cell" role="cell">
              {editable ? (
                <label className="srce-inline">
                  <input type="checkbox" checked={d.supplier_visible && d.informational} disabled={busy || !d.supplier_visible}
                    aria-label={`${d.file_name} is for information only`} onChange={() => toggleInfo(d)} />
                  {d.supplier_visible ? yesNo(d.informational) : 'Team only'}
                </label>
              ) : (d.supplier_visible ? yesNo(d.informational) : 'Team only')}
            </div>
            {editable && (
              <div className="srce-wt-cell srce-docs-acts" role="cell">
                <button type="button" className="srce-btn srce-btn--quiet" disabled={busy}
                  onClick={() => { setReplacing(d.document_id); if (replaceRef.current) replaceRef.current.click(); }}>Replace</button>
                <button type="button" className="srce-btn srce-btn--quiet" disabled={busy}
                  onClick={() => run(() => api.documentAction(eventId, d.document_id, 'remove', {}, summary.revision))}>Remove</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
